import { getConditionTypeLabel } from "@redotech/redo-flows-common/oms/conditions";
import { OmsConditionStep } from "@redotech/redo-flows-common/steps";
import { OmsConditionType } from "@redotech/redo-flows-core-common/oms-condition-type";
import {
  RedoShopifyDeliveryMethodType,
  RedoShopifyDeliveryMethodTypeToString,
} from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import { t, useTranslation } from "@redotech/redo-i18n";
import BranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { Provider } from "@redotech/redo-order-common/provider";
import { orderProviderToLabel } from "@redotech/redo-order-core-common/order";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { Handle, Position } from "@xyflow/react";
import {
  DeepPartial,
  PartialFlowStep,
  PartialWithRequiredType,
} from "../../steps";
import * as NodeCSS from "../node.module.css";

import { Team } from "@redotech/redo-team-common/team";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useContext } from "react";
import { BaseNode } from "../base-node";
import { FieldToListMatchDetails } from "../common/field-to-list-match-details";
import { WeightComparisonDetails } from "../common/weight-comparison-details";
import { useFlipBranchesMenuItems } from "../use-flip-branches-menu-items";
import { DateComparisonDetails } from "./date-comparison-details";
import { IntegerComparisonDetails } from "./integer-comparison-details";
import { StringMatchDetails } from "./string-match-details";

export interface OmsConditionNodeData {
  step: PartialWithRequiredType<OmsConditionStep>;
  deleteElements: (id: string) => void;
  updateStep: (id: string, updatedStep: PartialFlowStep) => void;
  readonly: boolean;
}

export function getConditionIntroText(step: DeepPartial<OmsConditionStep>) {
  const conditionType = step.conditionType;
  switch (conditionType) {
    case OmsConditionType.AVAILABLE_INVENTORY:
    case OmsConditionType.ITEM_COST:
    case OmsConditionType.VENDOR:
      return t("If any product's");
    case OmsConditionType.FRAUD_RISK:
    case OmsConditionType.ORDER_TAG:
    case OmsConditionType.SELECTED_SHIPPING:
    case OmsConditionType.SKU:
    case OmsConditionType.PRODUCT_NAME:
    case OmsConditionType.VARIANT_TITLE:
    case OmsConditionType.PRODUCT_TYPE:
    case OmsConditionType.ITEM_QUANTITY:
    case OmsConditionType.PACKAGE_WEIGHT_OZ:
    case OmsConditionType.TOTAL_VALUE:
    case OmsConditionType.TAXES_PAID:
    case OmsConditionType.DESTINATION_COUNTRY:
    case OmsConditionType.DESTINATION_PROVINCE:
    case OmsConditionType.DELIVERY_METHOD:
    case OmsConditionType.USPS_ZONE:
    case OmsConditionType.STREET_ADDRESS:
    case OmsConditionType.STREET_ADDRESS_LIST_INPUT:
    case OmsConditionType.ZIP_CODE:
    case OmsConditionType.SALES_CHANNEL:
    case OmsConditionType.PRODUCT_TAG:
    case OmsConditionType.FULFILLMENT_LOCATION:
    case OmsConditionType.ORIGIN_STORE:
    case OmsConditionType.ORDER_NOTES:
    case OmsConditionType.MARKETPLACE_INTERNAL_NOTES:
    case OmsConditionType.MARKETPLACE:
    case OmsConditionType.SHIPMENT_TAG:
    case OmsConditionType.CUSTOM_STRING_1:
    case OmsConditionType.CUSTOM_STRING_2:
    case OmsConditionType.CUSTOM_STRING_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3:
    case OmsConditionType.CUSTOM_NUMBER_1:
    case OmsConditionType.CUSTOM_NUMBER_2:
    case OmsConditionType.CUSTOM_NUMBER_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3:
    case OmsConditionType.CUSTOM_DATE_1:
    case OmsConditionType.CUSTOM_DATE_2:
    case OmsConditionType.CUSTOM_DATE_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_1:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_2:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_3:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_1:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_2:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_3:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_1:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_2:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_3:
    case OmsConditionType.CUSTOMER_NAME:
    case OmsConditionType.CUSTOMER_ORDER_COUNT:
    case OmsConditionType.RECOGNIZE_SEGMENT:
    case OmsConditionType.CUSTOMER_TAG:
    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE:
    case OmsConditionType.ADDRESS_VERIFIED:
    case OmsConditionType.PACKAGE_IS_LEARNED:
    case OmsConditionType.DESTINATION_FORECAST_TEMPERATURE:
    case undefined:
      return t("If");
    default:
      assertNever(conditionType);
  }
}
function getDetails(
  step: DeepPartial<OmsConditionStep>,
  team: Team | undefined,
) {
  switch (step.conditionType) {
    case OmsConditionType.FRAUD_RISK:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.FRAUD_RISK)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues?.map((value) => value)}
        />
      );
    case OmsConditionType.AVAILABLE_INVENTORY:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(OmsConditionType.AVAILABLE_INVENTORY)}
          matchType={step.matchType}
          matchValue={step.matchValue}
        />
      );
    case OmsConditionType.ORDER_TAG:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.ORDER_TAG)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.SELECTED_SHIPPING:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.SELECTED_SHIPPING)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.SKU:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.SKU)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.PRODUCT_NAME:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.PRODUCT_NAME)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.VARIANT_TITLE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.VARIANT_TITLE)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.PRODUCT_TYPE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.PRODUCT_TYPE)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.ITEM_QUANTITY:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(OmsConditionType.ITEM_QUANTITY)}
          matchType={step.matchType}
          matchValue={step.matchValue}
        />
      );
    case OmsConditionType.PACKAGE_WEIGHT_OZ:
      return (
        <WeightComparisonDetails
          field={getConditionTypeLabel(OmsConditionType.PACKAGE_WEIGHT_OZ)}
          matchType={step.matchType}
          matchValue={step.matchValue}
        />
      );
    case OmsConditionType.TOTAL_VALUE:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(OmsConditionType.TOTAL_VALUE)}
          matchType={step.matchType}
          matchValue={step.matchValue}
          prefix="$"
        />
      );
    case OmsConditionType.TAXES_PAID:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(OmsConditionType.TAXES_PAID)}
          matchType={step.matchType}
          matchValue={step.matchValue}
          prefix="$"
        />
      );
    case OmsConditionType.DESTINATION_COUNTRY:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.DESTINATION_COUNTRY)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.DESTINATION_PROVINCE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.DESTINATION_PROVINCE)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.DELIVERY_METHOD:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.DELIVERY_METHOD)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues?.map(
            (method) =>
              RedoShopifyDeliveryMethodTypeToString[
                method as RedoShopifyDeliveryMethodType
              ] || method,
          )}
        />
      );
    case OmsConditionType.STREET_ADDRESS:
      return (
        <StringMatchDetails
          field={getConditionTypeLabel(OmsConditionType.STREET_ADDRESS)}
          matchType={step.matchType}
          matchValue={step.matchValue}
        />
      );
    case OmsConditionType.STREET_ADDRESS_LIST_INPUT:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(
            OmsConditionType.STREET_ADDRESS_LIST_INPUT,
          )}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.ZIP_CODE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.ZIP_CODE)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.SALES_CHANNEL:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.SALES_CHANNEL)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.PRODUCT_TAG:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.PRODUCT_TAG)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.VENDOR:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.VENDOR)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.FULFILLMENT_LOCATION:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.FULFILLMENT_LOCATION)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.ORIGIN_STORE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.ORIGIN_STORE)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.ORDER_NOTES:
      return (
        <StringMatchDetails
          field={getConditionTypeLabel(OmsConditionType.ORDER_NOTES)}
          matchType={step.matchType}
          matchValue={step.matchValue}
        />
      );
    case OmsConditionType.MARKETPLACE_INTERNAL_NOTES:
      return (
        <StringMatchDetails
          field={getConditionTypeLabel(
            OmsConditionType.MARKETPLACE_INTERNAL_NOTES,
          )}
          matchType={step.matchType}
          matchValue={step.matchValue}
        />
      );
    case OmsConditionType.MARKETPLACE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.MARKETPLACE)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues?.map(
            (provider) =>
              orderProviderToLabel[provider as Provider] || provider,
          )}
        />
      );
    case OmsConditionType.SHIPMENT_TAG:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.SHIPMENT_TAG)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.CUSTOMER_TAG:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.CUSTOMER_TAG)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.RECOGNIZE_SEGMENT:
      return ` ${t("(legacy condition)")}`;
    case OmsConditionType.CUSTOM_STRING_1:
    case OmsConditionType.CUSTOM_STRING_2:
    case OmsConditionType.CUSTOM_STRING_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_1:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_2:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_3:
    case OmsConditionType.CUSTOMER_NAME:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(step.conditionType, team)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.CUSTOM_NUMBER_1:
    case OmsConditionType.CUSTOM_NUMBER_2:
    case OmsConditionType.CUSTOM_NUMBER_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_1:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_2:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_3:
    case OmsConditionType.CUSTOMER_ORDER_COUNT:
    case OmsConditionType.ITEM_COST:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(step.conditionType, team)}
          matchType={step.matchType}
          matchValue={step.matchValue}
          prefix={
            step.conditionType === OmsConditionType.ITEM_COST ? "$" : undefined
          }
        />
      );
    case OmsConditionType.CUSTOM_DATE_1:
    case OmsConditionType.CUSTOM_DATE_2:
    case OmsConditionType.CUSTOM_DATE_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_1:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_2:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_3:
      return (
        <DateComparisonDetails
          field={getConditionTypeLabel(step.conditionType, team)}
          matchDate={step.matchDate as Date}
          matchType={step.matchType}
        />
      );
    case OmsConditionType.USPS_ZONE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.USPS_ZONE)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues?.map((zone) => `Zone ${zone}`)}
        />
      );
    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE:
      return (
        <FieldToListMatchDetails
          field={`${getConditionTypeLabel(OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE)}${step.matchKey ? ` "${step.matchKey}"` : ""}`}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.ADDRESS_VERIFIED:
      return (
        <Text>
          {getConditionTypeLabel(OmsConditionType.ADDRESS_VERIFIED)}{" "}
          <Text fontWeight="semibold">
            {step.matchValue === true
              ? t("is verified")
              : step.matchValue === false
                ? t("is not verified")
                : "..."}
          </Text>
        </Text>
      );
    case OmsConditionType.PACKAGE_IS_LEARNED:
      return (
        <Text>
          {getConditionTypeLabel(OmsConditionType.PACKAGE_IS_LEARNED)}{" "}
          <Text fontWeight="semibold">
            {step.matchValue === true
              ? t("Yes")
              : step.matchValue === false
                ? t("No")
                : "..."}
          </Text>
        </Text>
      );
    case OmsConditionType.DESTINATION_FORECAST_TEMPERATURE:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(
            OmsConditionType.DESTINATION_FORECAST_TEMPERATURE,
          )}
          matchType={step.matchType}
          matchValue={step.matchValue}
          suffix="°F"
        />
      );
    case undefined:
      return <Text textColor="disabled">{t("Choose a condition")}</Text>;
    default:
      assertNever(step);
  }
}

// search-tag:ADDING_AN_OMS_CONDITION
export function OmsConditionNode(data: {
  id: string;
  data: OmsConditionNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const additionalMenuItems = useFlipBranchesMenuItems({
    id: data.id,
    step: data.data.step,
    readonly: data.data.readonly,
    updateStep: data.data.updateStep,
  });

  return (
    <BaseNode
      additionalMenuItems={additionalMenuItems}
      icon={<RedoFeaturedIcon color="warning" Icon={BranchIcon} size="xs" />}
      onDelete={
        data.data.readonly ? undefined : () => data.data.deleteElements(data.id)
      }
      selected={data.selected}
      step={data.data.step}
      title={t("Condition")}
    >
      <>
        <Handle position={Position.Top} type="target" />
        <Flex
          className={NodeCSS.fullInput}
          fontSize="xs"
          gap="none"
          wrap="wrap"
        >
          {getConditionIntroText(data.data.step) + " "}
          {getDetails(data.data.step, team)}
          {data.data.step.where?.length ? (
            <Text
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {` ${t("where")} `}
              {getDetails(
                data.data.step.where[0] as DeepPartial<OmsConditionStep>, //FIXME once OMS is fully using the new advanced flow zod types
                team,
              )}
            </Text>
          ) : undefined}
        </Flex>
        <Handle id="a" position={Position.Bottom} type="source" />
        <Handle id="b" position={Position.Bottom} type="source" />
      </>
    </BaseNode>
  );
}
